import type { Product } from '../types';
import { normalizeProduct, normalizeProductList } from '../utils/normalizeProduct';
import { apiRequest, AuthApiError } from './authApi';

export interface CreateProductBody {
  name: string;
  description: string;
  price: number;
  category: string;
  image?: string;
  stock?: number;
  inStock?: boolean;
  featured?: boolean;
  originalPrice?: number;
}

export interface CreateReviewBody {
  rating: number;
  title: string;
  comment: string;
}

const PUBLIC_PRODUCTS_TTL_MS = 60_000;
const PUBLIC_PRODUCT_TTL_MS = 120_000;

const publicProductsCache = new Map<string, { expiresAt: number; data: Product[] }>();
const publicProductsInFlight = new Map<string, Promise<Product[]>>();
const publicProductByIdCache = new Map<string, { expiresAt: number; data: Product | null }>();
const publicProductByIdInFlight = new Map<string, Promise<Product | null>>();

export function publicProductsCacheKey(category?: string): string {
  const t = category?.trim();
  return t && t !== 'All' ? t.toLowerCase() : '__all__';
}

export function invalidatePublicProductsCache(): void {
  publicProductsCache.clear();
  publicProductsInFlight.clear();
  publicProductByIdCache.clear();
  publicProductByIdInFlight.clear();
}

/** Look up a product in any warm list cache (avoids extra GET /products/:id). */
export function findCachedProductById(id: string): Product | null {
  let found: Product | null = null;
  publicProductsCache.forEach((entry) => {
    if (found || entry.expiresAt <= Date.now()) return;
    const hit = entry.data.find((p) => p.id === id);
    if (hit) found = hit;
  });
  return found;
}

/** `GET /products` — public catalog; optional `category` matches backend `?category=` (case-insensitive). */
export async function fetchPublicProducts(category?: string): Promise<Product[]> {
  const key = publicProductsCacheKey(category);
  const now = Date.now();
  const hit = publicProductsCache.get(key);
  if (hit && hit.expiresAt > now) {
    return hit.data;
  }

  let inflight = publicProductsInFlight.get(key);
  if (!inflight) {
    const qs =
      category !== undefined && category.trim().length > 0 && category.trim() !== 'All'
        ? `?category=${encodeURIComponent(category.trim())}`
        : '';
    inflight = (async () => {
      try {
        const raw = await apiRequest<unknown>(`/products${qs}`, {
          method: 'GET',
          accessToken: null,
        });
        const data = normalizeProductList(raw);
        publicProductsCache.set(key, { data, expiresAt: Date.now() + PUBLIC_PRODUCTS_TTL_MS });
        return data;
      } finally {
        publicProductsInFlight.delete(key);
      }
    })();
    publicProductsInFlight.set(key, inflight);
  }
  return inflight;
}

/** `GET /products/:id` — uses list cache when possible, then dedicated id cache. */
export async function fetchPublicProductById(id: string): Promise<Product | null> {
  const now = Date.now();
  const idHit = publicProductByIdCache.get(id);
  if (idHit && idHit.expiresAt > now) {
    return idHit.data;
  }

  const fromList = findCachedProductById(id);
  if (fromList) {
    publicProductByIdCache.set(id, {
      data: fromList,
      expiresAt: Date.now() + PUBLIC_PRODUCT_TTL_MS,
    });
    return fromList;
  }

  let inflight = publicProductByIdInFlight.get(id);
  if (!inflight) {
    inflight = (async () => {
      try {
        const raw = await apiRequest<unknown>(`/products/${encodeURIComponent(id)}`, {
          method: 'GET',
          accessToken: null,
        });
        const p = normalizeProduct(raw);
        publicProductByIdCache.set(id, {
          data: p,
          expiresAt: Date.now() + PUBLIC_PRODUCT_TTL_MS,
        });
        return p;
      } finally {
        publicProductByIdInFlight.delete(id);
      }
    })();
    publicProductByIdInFlight.set(id, inflight);
  }
  return inflight;
}

function buildProductWritePayload(body: CreateProductBody): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: body.name,
    description: body.description,
    price: body.price,
    stock: body.stock ?? 0,
  };
  if (body.category?.trim()) payload.category = body.category.trim();
  if (body.image) payload.imageUrl = body.image;
  if (body.originalPrice !== undefined) payload.originalPrice = body.originalPrice;
  if (body.inStock !== undefined) payload.isActive = body.inStock;
  return payload;
}

/** `POST /products` — ADMIN + JWT */
export async function createProductAdmin(body: CreateProductBody): Promise<Product> {
  const raw = await apiRequest<unknown>('/products', {
    method: 'POST',
    body: JSON.stringify(buildProductWritePayload(body)),
  });
  const p = normalizeProduct(raw);
  if (!p) throw new AuthApiError('Invalid create product response', 500, raw);
  invalidatePublicProductsCache();
  return p;
}

/** `PATCH /products/:id` — ADMIN + JWT */
export async function updateProductAdmin(id: string, body: CreateProductBody): Promise<Product> {
  const raw = await apiRequest<unknown>(`/products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(buildProductWritePayload(body)),
  });
  const p = normalizeProduct(raw);
  if (!p) throw new AuthApiError('Invalid update product response', 500, raw);
  invalidatePublicProductsCache();
  return p;
}

/** `DELETE /products/:id` — ADMIN + JWT */
export async function deleteProductAdmin(id: string): Promise<void> {
  await apiRequest<unknown>(`/products/${encodeURIComponent(id)}`, { method: 'DELETE' });
  invalidatePublicProductsCache();
}

/** `POST /products/:id/reviews` — authenticated user review */
export async function createProductReview(id: string, body: CreateReviewBody): Promise<void> {
  await apiRequest<unknown>(`/products/${encodeURIComponent(id)}/reviews`, {
    method: 'POST',
    body: JSON.stringify({
      rating: body.rating,
      title: body.title.trim(),
      comment: body.comment.trim(),
      content: body.comment.trim(),
    }),
  });
  invalidatePublicProductsCache();
}
